// W31 (2026-10-20): real identity for the W2A-lower screens (Customer
// Addresses, Edit Preferences, Special Follow-ups, Calls & Callbacks,
// Live Chat, Intake, Incoming Email, Incoming Text).
//
// Wraps the W25/W26 `useContactProfile` hook (screens/_shared, read-only
// for this wave) and returns the IdentityRow-shaped record the approved
// chrome expects. The SARAH design fixture is reachable ONLY when the
// session is the dev mock or the route carries ?preview=; every other
// state (session pending, loading, error, empty, ready) renders real
// data or an honest placeholder.
//
// Lives in _w2a_lower (W31's lane), not _shared (W32 is adding a
// sibling `useReviewIdentity` there for the customer sub-tabs). The two
// can be consolidated once both waves have merged.
import { useMemo } from 'react';
import { isDevMockSession } from '../../auth/mockContext.js';
import { useOptionalSession } from '../../auth/session-context.js';
import { usePreviewFixtureMode } from '../_shared/useAuthenticatedCustomer.js';
import {
  useContactProfile,
  type ContactProfileRecord,
  type ContactProfileStatus,
} from '../_shared/useContactProfile.js';
import { SARAH, type MockCustomer } from '../../data/mockData.js';

export interface ScreenIdentity {
  /** True only in ?preview= / dev-mock mode: the design fixture may render. */
  isPreview: boolean;
  /** True on every real session: Save / Send buttons are inert. */
  liveWritesPaused: boolean;
  status: ContactProfileStatus;
  profile: ContactProfileRecord | null;
  /** IdentityRow record: SARAH in preview, real contact or honest placeholder otherwise. */
  customer: MockCustomer;
  /** Replaces the "Customer since" line on a real session. */
  sinceLabel: string | undefined;
  /** First name for copy; "this customer" until the profile is ready. */
  firstName: string;
  /** Possessive form for copy: "Jodi's" or "this customer's". */
  possessive: string;
  /**
   * VanillaSoft callback date (HubSpot `call_back_date_vs`) when the
   * api-server exposes it as `callBackDateVs` (W34 adds the property;
   * W26's payload does not carry it, so this is null today). Never
   * fabricated: only a non-empty string from the profile is returned.
   */
  callBackDate: string | null;
}

export function monthYear(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Map the W25/W26 contact profile to the IdentityRow record. */
export function toCustomerRecord(c: ContactProfileRecord): MockCustomer {
  return {
    id: c.id,
    initials: c.initials || c.id.slice(0, 2).toUpperCase(),
    firstName: c.firstName || c.fullName.split(' ')[0] || c.id,
    fullName: c.fullName,
    customerSince: monthYear(c.createdAt) ?? 'date unknown',
    city: c.city || '',
    stateCode: c.state || '',
    localTime: '',
    phone: c.phone || '',
    email: c.email || '',
    // W38: the real HubSpot `preferred_channel` value, or '' so the
    // IdentityRow renders "not set" (it is set on 0 contacts today,
    // MEASURED in the W30 audit). Never the fixture's "Phone call".
    preferredChannel: readPreferredChannel(c),
  };
}

/** HubSpot `preferred_channel` from the profile payload, '' when unset. */
export function readPreferredChannel(profile: ContactProfileRecord | null): string {
  const raw = profile?.preferredChannel;
  return typeof raw === 'string' ? raw.trim() : '';
}

function placeholderRecord(customerId: string | null, label: string): MockCustomer {
  const id = customerId ?? '';
  return {
    id,
    initials: id ? id.slice(0, 2).toUpperCase() : '',
    firstName: '',
    fullName: label,
    customerSince: 'date unknown',
    city: '',
    stateCode: '',
    localTime: '',
    phone: '',
    email: '',
    preferredChannel: '',
  };
}

export function readCallBackDate(profile: ContactProfileRecord | null): string | null {
  if (!profile) return null;
  const raw = (profile as { callBackDateVs?: unknown }).callBackDateVs;
  return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
}

export function useScreenIdentity(customerId: string | null | undefined): ScreenIdentity {
  const sessionCtx = useOptionalSession();
  const previewFlag = usePreviewFixtureMode();
  const devMock = isDevMockSession(sessionCtx?.session ?? null);
  const isPreview = previewFlag || devMock;
  const profile = useContactProfile(customerId);
  const id = customerId ?? null;

  return useMemo<ScreenIdentity>(() => {
    if (isPreview) {
      return {
        isPreview: true,
        liveWritesPaused: false,
        status: profile.status,
        profile: null,
        customer: SARAH,
        sinceLabel: undefined,
        firstName: SARAH.firstName,
        possessive: `${SARAH.firstName}’s`,
        callBackDate: null,
      };
    }
    const c = profile.contact;
    if (profile.status === 'ready' && c) {
      const customer = toCustomerRecord(c);
      const sinceLabel = c.lastCallDate
        ? `Last VanillaSoft call ${c.lastCallDate}`
        : c.createdAt
          ? `In HubSpot since ${monthYear(c.createdAt)}`
          : ' ';
      return {
        isPreview: false,
        liveWritesPaused: true,
        status: profile.status,
        profile: c,
        customer,
        sinceLabel,
        firstName: customer.firstName,
        possessive: `${customer.firstName}’s`,
        callBackDate: readCallBackDate(c),
      };
    }
    const label = !id
      ? 'No contact in scope'
      : profile.status === 'loading' || profile.status === 'skipped'
        ? 'Loading contact…'
        : profile.status === 'empty'
          ? `No HubSpot contact ${id}`
          : 'Contact could not load';
    return {
      isPreview: false,
      liveWritesPaused: true,
      status: profile.status,
      profile: null,
      customer: placeholderRecord(id, label),
      sinceLabel: ' ',
      firstName: 'this customer',
      possessive: 'this customer’s',
      callBackDate: null,
    };
  }, [isPreview, profile.status, profile.contact, id]);
}

/** Narrow helper shared by the real-body mappers. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function readOptionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}
